import { usePublicBootstrap } from '../../../seo/PublicBootstrap.jsx';
import React, { useState, useEffect } from 'react';
import { Card } from '@mts/ui';
import { Pencil } from 'lucide-react';

import { DAY_NAMES, formatHoursValue, getStatusLabel } from '../../../utils/businessHoursPresentation.js';

function BasicBusinessHoursCard({ hoursRows, hoursField, canEdit = false, onEdit }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const bootstrap = usePublicBootstrap();
  const [now, setNow] = useState(() => new Date(bootstrap?.renderedAt || Date.now()));
  useEffect(() => {
    setNow(new Date());
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);
  let timezone = bootstrap?.tenant?.timezone || undefined;
  try { if (timezone) new Intl.DateTimeFormat('en-US', { timeZone: timezone }).format(now); } catch { timezone = undefined; }
  const currentDayOfWeek = timezone ? DAY_NAMES.indexOf(new Intl.DateTimeFormat('en-US', { timeZone: timezone, weekday: 'short' }).format(now)) : bootstrap ? -1 : now.getDay();

  const normalizedRows = Array.isArray(hoursRows)
    ? hoursRows
      .filter((row) => Number.isInteger(row?.dayOfWeek) && row.dayOfWeek >= 0 && row.dayOfWeek <= 6)
      .sort((a, b) => a.dayOfWeek - b.dayOfWeek)
    : [];

  const statusLabel = normalizedRows.length === 0 ? 'Hours unavailable' : bootstrap && !timezone ? 'Business hours' : getStatusLabel(normalizedRows, now, timezone);

  return (
    <div id="BusinessHoursCard">
      <Card
        id="BusinessHoursCard-Card"
        className="my-[10px] rounded-lg"
        style={{
          backgroundColor: 'var(--card)',
          borderColor: 'var(--border)',
          borderWidth: '1px',
          borderStyle: 'solid',
        }}
      >
      <Card.Header id="BusinessHoursCard-CardHeader" className="flex items-start justify-between gap-3">
        <div id="BusinessHoursCard-CardHeaderInner">
          <h2 className="text-base font-semibold text-[var(--primary)]">Business Hours</h2>
          <Card.Title id="BusinessHoursCard-CardTitle">
            <span className="sr-only">Business hours</span>
          </Card.Title>
          <Card.Description id="BusinessHoursCard-CardDescription">
            <span className="sr-only">Open and close times</span>
          </Card.Description>
        </div>
        <Card.Action id="BusinessHoursCard-CardAction">
          {canEdit ? (
            <button
              type="button"
              aria-label="Edit Business Hours"
              title="Edit Business Hours"
              onClick={onEdit}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[var(--border)] text-[var(--primary)] transition-colors hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary)]/30"
            >
              <Pencil size={16} aria-hidden="true" />
            </button>
          ) : (
            <div />
          )}
        </Card.Action>
      </Card.Header>

      <Card.Content id="BusinessHoursCard-CardContent" className="before:content-none after:content-none">
      {normalizedRows.length > 0 ? (
        <div title={`listing field: ${hoursField}`}>
          <div className="space-y-1.5">
            <button
              type="button"
              onClick={() => setIsExpanded((current) => !current)}
              aria-expanded={isExpanded}
              className="flex w-full items-center justify-between rounded-md px-2 py-2 text-left text-sm font-semibold text-black"
            >
              <span>{statusLabel}</span>
              <span className="text-base leading-none" aria-hidden="true">{isExpanded ? '▴' : '▾'}</span>
            </button>

            {isExpanded
              ? normalizedRows.map((row) => {
                  const isCurrentDay = row.dayOfWeek === currentDayOfWeek;

                  return (
                    <div
                      key={`${row.dayOfWeek}-${row.openTime || 'closed'}`}
                      className={`grid grid-cols-[44px,1fr] gap-3 rounded-md px-2 py-1 text-sm ${
                        isCurrentDay ? 'bg-[var(--primary-soft)] font-semibold text-black' : ''
                      }`}
                    >
                      <span className="font-medium text-black/75">{DAY_NAMES[row.dayOfWeek] || 'Day'}</span>
                      <span className="text-black/80">{formatHoursValue(row)}</span>
                    </div>
                  );
                })
              : null}
          </div>
        </div>
      ) : (
        <p className="text-sm text-black/70">Business hours not available.</p>
      )}
      </Card.Content>

      <Card.Footer id="BusinessHoursCard-CardFooter">
        <div />
      </Card.Footer>
      </Card>
    </div>
  );
}

export default BasicBusinessHoursCard;
